interface Phone {
  number?: string;
  iso2?: string;
  dialCode?: string;
}

export type PhoneNumberType =
  | 'mobile'
  | 'landline'
  | 'special6'
  | 'special7'
  | 'special10'
  | 'unknown';

export const phoneNumberTypeGroupSizes: Record<PhoneNumberType, number[]> = {
  mobile: [3, 3, 3],
  landline: [1, 4, 4],
  special6: [2, 2, 2],
  special7: [4, 3],
  special10: [4, 3, 3],
  unknown: [],
};

function getPhoneType(
  phone: string,
): 'mobile' | 'landline' | 'special6' | 'special7' | 'special10' | 'unknown' {
  if (phone.length === 9) {
    // if the phone starts with a 4 then it is a mobile number
    if (phone.startsWith('4')) {
      return 'mobile';
    }

    // if the phone starts with any of 8,7,3,2 then it is a landline number
    if (
      phone.startsWith('8') ||
      phone.startsWith('7') ||
      phone.startsWith('3') ||
      phone.startsWith('2')
    ) {
      return 'landline';
    }
  }

  // if a phone number starts with 1300 or 1800 it is a special 10 digit number
  if (
    (phone.startsWith('1300') || phone.startsWith('1800')) &&
    phone.length === 10
  ) {
    return 'special10';
  }

  // if a phone number starts with 13 it is a special 6 digit number
  if (phone.startsWith('13') && phone.length === 6) {
    return 'special6';
  }

  // if a phone number starts with 180 it is a special 7 digit number
  if (phone.startsWith('180')) {
    return 'special7';
  }

  return 'unknown';
}

function insertSpacesBetweenGroups(
  phone: string,
  groupSizes: number[],
): string {
  let result = '';
  let currentPosition = 0;

  // Process each group size
  for (const groupSize of groupSizes) {
    // If we've reached the end of the string, return what we have
    if (currentPosition >= phone.length) {
      return result.trim();
    }

    // Add the group of characters
    result += phone.substr(currentPosition, groupSize);
    currentPosition += groupSize;

    // Only add a space if we're not at the end of the string
    if (currentPosition < phone.length) {
      result += ' ';
    }
  }

  // If there are remaining characters, append them
  if (currentPosition < phone.length) {
    result += phone.substr(currentPosition);
  }

  return result;
}

function getUnknownNumberTypeSpacing(phone: string): number[] {
  // if the phone number length is a multiple of 4 the space in
  // groups of 4
  if (phone.length % 4 === 0) {
    return [4, 4, 4, 4];
  }

  // otherwise space in groups of 3
  return [3, 3, 3, 3, 3, 3, 3, 3, 3, 3];
}

export function formatPhoneNumberWithCountryCode(value?: Phone) {
  if (!value || !value.number || !value.number.trim()) {
    return 'NO NUMBER PROVIDED';
  }

  // remove any leading or trailing spaces from the phone string
  let fullPhone = value.number.trim();

  // If the phone contains a non-digit then return the string
  if (fullPhone.match(/\D/)) {
    return fullPhone;
  }

  // otherwise remove any internal spaces before formatting
  fullPhone = fullPhone.replace(/\s/g, '');

  // if there is a leading zero, remove it.
  const zeroStrippedPhone = fullPhone.replace(/^0/, '');

  if (value.iso2 === 'AU') {
    // for Australian phone numbers (the most common in this app)
    // decide how to space the phone string,
    // depending on the type they are generally spaced differently
    const phoneType = getPhoneType(zeroStrippedPhone);
    let groupSizes: number[];
    if (phoneType !== 'unknown') {
      groupSizes = phoneNumberTypeGroupSizes[phoneType];
    } else {
      groupSizes = getUnknownNumberTypeSpacing(fullPhone);
    }

    const spacedPhone = insertSpacesBetweenGroups(
      zeroStrippedPhone,
      groupSizes,
    );

    if (phoneType == 'mobile' || phoneType == 'landline') {
      return `0${spacedPhone}`;
    }

    return spacedPhone;
  }

  const spacedPhone = insertSpacesBetweenGroups(
    zeroStrippedPhone,
    getUnknownNumberTypeSpacing(zeroStrippedPhone),
  );

  if (value.dialCode && value.dialCode.trim()) {
    return `+${value.dialCode} ${spacedPhone}`;
  }

  return spacedPhone;
}
